import * as React from "react";
import { createPortal } from "react-dom";
import { Ban, Check, Download, GraduationCap } from "lucide-react";
import { Button } from "../Button";
import { ImageLoader } from "../ImageLoader";
import { useCertificationContentDefinitions } from "../../../app/lib/i18n/useCertificationContentDefinitions";
import { useT } from "../../../app/lib/i18n";
import * as styles from "./CertificationModulesCircularProgress.style.css";

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export type CertificationModuleCircleStatus =
  | "not_started"
  | "in_progress"
  | "retake_needed"
  | "complete"
  | string;

export type CertificationModuleCircleItem = {
  moduleId: string;
  moduleTitle: string;
  progressPercent: number;
  status: CertificationModuleCircleStatus;
  completedAt?: string | null;
  expiresAt?: string | null;
  certificateNumber?: string | null;
  trophyImageUrl?: string | null;
  heroImageUrl?: string | null;
  description?: string | null;
};

export type CertificationModuleCatalogItem = {
  id: string;
  title: string;
  trophyImageUrl?: string | null;
  heroImageUrl?: string | null;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
};

const RING_R = 15.5;
const RING_C = 2 * Math.PI * RING_R;
const OPEN_DELAY_MS = 800;
const CLOSE_DELAY_MS = 120;
const VIEWPORT_PAD = 8;
const GAP = 10;

const STATUS_LABELS: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  retake_needed: "Retake needed",
  complete: "Complete",
  expired: "Expired",
};

function isExpired(
  expiresAt: string | null | undefined,
  now = Date.now(),
): boolean {
  if (!expiresAt) return false;
  const ms = new Date(expiresAt).getTime();
  return !Number.isNaN(ms) && ms <= now;
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export type ModuleCircleVisualState =
  | "not_started"
  | "in_progress"
  | "retake_needed"
  | "complete"
  | "expired";

export function getModuleCircleVisualState(
  mod: Pick<
    CertificationModuleCircleItem,
    "status" | "expiresAt" | "progressPercent"
  >,
): ModuleCircleVisualState {
  if (isExpired(mod.expiresAt)) return "expired";
  if (mod.status === "complete") return "complete";
  if (mod.status === "retake_needed") return "retake_needed";
  if (mod.status === "in_progress" || mod.progressPercent > 0) {
    return "in_progress";
  }
  return "not_started";
}

/**
 * Merge a module catalog with per-user progress so every module appears,
 * including not-yet-started ones.
 */
export function mergeCertificationModuleCircles(
  catalog: CertificationModuleCatalogItem[],
  progress: CertificationModuleCircleItem[],
): CertificationModuleCircleItem[] {
  const byId = new Map(progress.map((p) => [p.moduleId, p]));
  const activeCatalog = [...catalog]
    .filter((m) => m.isActive !== false)
    .sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.title.localeCompare(b.title),
    );

  const merged: CertificationModuleCircleItem[] = activeCatalog.map((m) => {
    const p = byId.get(m.id);
    return {
      moduleId: m.id,
      moduleTitle: m.title,
      trophyImageUrl: m.trophyImageUrl ?? p?.trophyImageUrl ?? null,
      heroImageUrl: m.heroImageUrl ?? p?.heroImageUrl ?? null,
      description: m.description ?? p?.description ?? null,
      progressPercent: p?.progressPercent ?? 0,
      status: p?.status ?? "not_started",
      completedAt: p?.completedAt ?? null,
      expiresAt: p?.expiresAt ?? null,
      certificateNumber: p?.certificateNumber ?? null,
    };
  });

  for (const p of progress) {
    if (!merged.some((m) => m.moduleId === p.moduleId)) {
      merged.push(p);
    }
  }

  return merged;
}

function fillClassForState(state: ModuleCircleVisualState): string {
  switch (state) {
    case "complete":
      return styles.ringFillComplete;
    case "retake_needed":
      return styles.ringFillRetake;
    case "expired":
      return styles.ringFillExpired;
    case "in_progress":
      return styles.ringFillInProgress;
    default:
      return styles.ringFillNotStarted;
  }
}

function statusChipClass(state: ModuleCircleVisualState): string {
  switch (state) {
    case "complete":
      return styles.modulePreviewStatusComplete;
    case "in_progress":
      return styles.modulePreviewStatusInProgress;
    case "retake_needed":
      return styles.modulePreviewStatusRetake;
    case "expired":
      return styles.modulePreviewStatusExpired;
    default:
      return "";
  }
}

function ModulePreviewPopover({
  mod,
  displayTitle,
  displayDescription,
  state,
  percent,
  anchorRect,
  onMouseEnter,
  onMouseLeave,
  onDownloadCertificate,
}: {
  mod: CertificationModuleCircleItem;
  displayTitle: string;
  displayDescription: string | null;
  state: ModuleCircleVisualState;
  percent: number;
  anchorRect: DOMRect;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onDownloadCertificate?: (moduleId: string) => void;
}) {
  const t = useT();
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const completedLabel = mod.completedAt ? shortDate(mod.completedAt) : null;
  const expiresLabel = mod.expiresAt ? shortDate(mod.expiresAt) : null;

  React.useLayoutEffect(() => {
    const update = () => {
      const popover = popoverRef.current;
      const width = popover?.offsetWidth ?? 300;
      const height = popover?.offsetHeight ?? 0;

      let left = anchorRect.left + anchorRect.width / 2 - width / 2;
      let top = anchorRect.bottom + GAP;

      if (left + width > window.innerWidth - VIEWPORT_PAD) {
        left = window.innerWidth - width - VIEWPORT_PAD;
      }
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

      if (top + height > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(VIEWPORT_PAD, anchorRect.top - height - GAP);
      }

      setPosition({ top, left });
    };

    update();
    const raf = requestAnimationFrame(update);
    const ro = popoverRef.current
      ? new ResizeObserver(() => update())
      : null;
    if (popoverRef.current && ro) ro.observe(popoverRef.current);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorRect]);

  return createPortal(
    <div
      ref={popoverRef}
      role="tooltip"
      className={styles.modulePreviewPopover}
      style={
        position
          ? { top: position.top, left: position.left, opacity: 1 }
          : { top: 0, left: 0, opacity: 0, pointerEvents: "none" }
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className={styles.modulePreviewHero}>
        {mod.heroImageUrl ? (
          <ImageLoader
            src={mod.heroImageUrl}
            alt=""
            objectFit="cover"
            className={styles.modulePreviewHeroImage}
          />
        ) : (
          <GraduationCap size={40} style={{ opacity: 0.55 }} />
        )}
      </div>
      <div className={styles.modulePreviewBody}>
        <p className={styles.modulePreviewTitle}>{displayTitle}</p>
        <span
          className={[styles.modulePreviewStatus, statusChipClass(state)]
            .filter(Boolean)
            .join(" ")}
        >
          {STATUS_LABELS[state] ?? state}
        </span>
        <div className={styles.modulePreviewMeta}>
          <p className={styles.modulePreviewMetaRow}>
            <span className={styles.modulePreviewMetaLabel}>{t("shared.certification.progressLabel")}</span>
            {percent}%
          </p>
          {completedLabel ? (
            <p className={styles.modulePreviewMetaRow}>
              <span className={styles.modulePreviewMetaLabel}>
                Completed:{" "}
              </span>
              {completedLabel}
            </p>
          ) : null}
          {state === "complete" && mod.certificateNumber ? (
            <p className={styles.modulePreviewMetaRow}>
              <span className={styles.modulePreviewMetaLabel}>
                {t("shared.certification.certificateNumberLabel")}{" "}
              </span>
              {mod.certificateNumber}
            </p>
          ) : null}
          {expiresLabel ? (
            <p className={styles.modulePreviewMetaRow}>
              <span className={styles.modulePreviewMetaLabel}>
                {state === "expired" ? "Expired: " : "Expires: "}
              </span>
              {expiresLabel}
            </p>
          ) : null}
          {displayDescription ? (
            <p className={styles.modulePreviewMetaRow}>{displayDescription}</p>
          ) : null}
        </div>
        {state === "complete" && onDownloadCertificate ? (
          <Button
            color="neutral"
            variant="outline"
            size="sm"
            className={styles.modulePreviewAction}
            iconLeft={<Download />}
            onClick={() => onDownloadCertificate(mod.moduleId)}
          >
            {t("shared.certification.downloadCertificate")}
          </Button>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function ModuleCircle({
  mod,
  onDownloadCertificate,
}: {
  mod: CertificationModuleCircleItem;
  onDownloadCertificate?: (moduleId: string) => void;
}) {
  const { resolve } = useCertificationContentDefinitions();
  const displayTitle = resolve(mod.moduleTitle).trim() || mod.moduleTitle;
  const displayDescription = mod.description
    ? resolve(mod.description).trim() || mod.description
    : null;

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const closeTimer = React.useRef<number | null>(null);

  const state = getModuleCircleVisualState(mod);
  const percent = clampPercent(mod.progressPercent);
  const offset = RING_C * (1 - percent / 100);
  const fillClass = fillClassForState(state);

  const clearTimers = React.useCallback(() => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  const scheduleOpen = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (open) return;
    if (openTimer.current != null) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) {
        setAnchorRect(rect);
        setOpen(true);
      }
    }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
    }
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const keepOpen = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  let center: React.ReactNode;
  if (state === "complete") {
    center = <Check size={16} strokeWidth={3} color="#059669" aria-hidden />;
  } else if (state === "expired") {
    center = <Ban size={15} strokeWidth={2.5} color="#6B7280" aria-hidden />;
  } else if (state === "not_started") {
    center = (
      <span className={[styles.ringPercent, styles.ringPercentMuted].join(" ")}>
        0
      </span>
    );
  } else {
    center = <span className={styles.ringPercent}>{percent}</span>;
  }

  const ariaLabel = `${displayTitle}: ${
    state === "expired"
      ? "expired"
      : state === "complete"
        ? "complete"
        : `${percent}%`
  }`;

  return (
    <>
      <div
        ref={anchorRef}
        className={styles.item}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div
          className={styles.ringWrap}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={ariaLabel}
        >
          <svg className={styles.ringSvg} viewBox="0 0 36 36" aria-hidden>
            <circle className={styles.ringTrack} cx="18" cy="18" r={RING_R} />
            <circle
              className={[styles.ringFill, fillClass].join(" ")}
              cx="18"
              cy="18"
              r={RING_R}
              strokeDasharray={RING_C}
              strokeDashoffset={state === "not_started" ? RING_C : offset}
            />
          </svg>
          <div className={styles.ringCenter}>{center}</div>
        </div>
        <p className={styles.label}>{displayTitle}</p>
      </div>
      {open && anchorRect ? (
        <ModulePreviewPopover
          mod={mod}
          displayTitle={displayTitle}
          displayDescription={displayDescription}
          state={state}
          percent={percent}
          anchorRect={anchorRect}
          onMouseEnter={keepOpen}
          onMouseLeave={scheduleClose}
          onDownloadCertificate={onDownloadCertificate}
        />
      ) : null}
    </>
  );
}

export function CertificationModulesCircularProgress({
  modules,
  onDownloadCertificate,
}: {
  modules: CertificationModuleCircleItem[];
  onDownloadCertificate?: (moduleId: string) => void;
}) {
  if (modules.length === 0) return null;

  return (
    <div className={styles.row}>
      {modules.map((mod) => (
        <ModuleCircle
          key={mod.moduleId}
          mod={mod}
          onDownloadCertificate={onDownloadCertificate}
        />
      ))}
    </div>
  );
}
